import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faCheck,
	faChevronDown,
	faClock,
	faSearch,
	faSpinner,
	faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Combobox, Transition } from '@headlessui/react';
import { debounce } from 'lodash';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useKeyboardAwareFloating } from '../../hooks/useKeyboardAwareFloating';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Badge, type BadgeVariant } from '../display/Badge';

// Types
export interface SelectSearchOption {
	value: string | number;
	label: string;
	subtitle?: string;
	/**
	 * PD-15466 — an optional THIRD line, rendered under `subtitle` in the same
	 * secondary style. Two supporting values (e.g. a customer number and a
	 * city/state) read far better stacked than crammed onto one line, which
	 * matters most when the label itself is long enough to truncate.
	 */
	detail?: string;
	/**
	 * PD-15466 — optional small status chip rendered after the label (e.g.
	 * Lead / Customer), so an option can carry its record's state without
	 * spending a text line on it. The label goes through t() like the other
	 * option text; colors follow the Badge variants.
	 */
	badge?: {
		label: string;
		variant?: BadgeVariant;
	};
	disabled?: boolean;
}

export interface SelectSearchGroup {
	key: string;
	label: string;
	icon?: IconDefinition;
	options: Array<SelectSearchOption>;
}

interface SelectSearchProps {
	// Core props
	value?: string | number | null;
	onChange?: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	error?: boolean;
	fullWidth?: boolean;
	className?: string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	clearable?: boolean;

	// Static mode props
	groups?: Array<SelectSearchGroup>;
	emptyMessage?: string;

	// Dynamic mode props
	mode?: 'static' | 'dynamic';
	loadRecent?: () => Promise<SelectSearchOption[]>;
	searchItems?: (query: string) => Promise<SelectSearchOption[]>;
	loadItemById?: (id: string) => Promise<SelectSearchOption | null>;
	staticGroups?: Array<SelectSearchGroup>; // Static groups to always show in dynamic mode

	// Dynamic mode configuration
	searchDebounceMs?: number;
	minSearchLength?: number;
	maxRecentItems?: number;

	// Messages
	loadingMessage?: string;
	searchingMessage?: string;
	emptyRecentMessage?: string;
	emptySearchMessage?: string;

	/**
	 * PD-15466 — Tailwind max-height for the dropdown, e.g. `max-h-96`. The
	 * default fits about five single-line options; raise it when the options
	 * carry a `subtitle` and a `detail`, since three-line rows otherwise leave
	 * barely two visible and the list reads as cut off.
	 */
	maxHeightClassName?: string;

	// Group labels for dynamic mode
	recentGroupLabel?: string;
	recentGroupIcon?: IconDefinition;
	searchGroupLabel?: string;
	searchGroupIcon?: IconDefinition;

	/**
	 * Called when the user presses Enter with a non-empty query and has NOT
	 * arrow-navigated to a specific option. Lets a consumer submit the raw typed
	 * text (e.g. a place text search) on Enter — without waiting for async
	 * options to load or requiring a click on the dropdown.
	 */
	onEnterKey?: (query: string) => void;
}

// Ensure portal container exists
if (
	typeof window !== 'undefined' &&
	!document.getElementById('select-search-portal')
) {
	const portalDiv = document.createElement('div');
	portalDiv.id = 'select-search-portal';
	portalDiv.style.position = 'fixed';
	portalDiv.style.top = '0';
	portalDiv.style.left = '0';
	portalDiv.style.zIndex = '9999';
	portalDiv.style.pointerEvents = 'none';
	document.body.appendChild(portalDiv);
}

export const SelectSearch = React.forwardRef<
	HTMLInputElement,
	SelectSearchProps
>(
	(
		{
			value,
			onChange,
			placeholder = 'Search...',
			disabled = false,
			error = false,
			fullWidth = true,
			className,
			size = 'md',
			clearable = false,

			// Static mode
			groups = [],
			emptyMessage = 'No options found',

			// Dynamic mode
			mode = 'static',
			loadRecent,
			searchItems,
			loadItemById,
			staticGroups = [],

			// Configuration
			searchDebounceMs = 300,
			minSearchLength = 2,
			maxRecentItems = 10,

			// Messages
			loadingMessage = 'Loading...',
			searchingMessage = 'Searching...',
			emptyRecentMessage = 'No recent items',
			emptySearchMessage = 'No results found',

			maxHeightClassName = 'max-h-60',

			// Group labels
			recentGroupLabel = 'Recent',
			recentGroupIcon = faClock,
			searchGroupLabel = 'Search Results',
			searchGroupIcon = faSearch,
			onEnterKey,
		},
		ref,
	) => {
		const colors = useThemeColors();
		const { t } = useTranslation();

		// State
		const [selectedOption, setSelectedOption] =
			useState<SelectSearchOption | null>(null);
		const [query, setQuery] = useState('');
		// Tracks whether the user arrow-navigated the options (so Enter should
		// select the active one) vs. just typed (so Enter can submit the raw
		// query via onEnterKey). Reset on every keystroke.
		const hasNavigatedRef = useRef(false);
		const [recentItems, setRecentItems] = useState<SelectSearchOption[]>([]);
		const [searchResults, setSearchResults] = useState<SelectSearchOption[]>(
			[],
		);
		const [isLoadingRecent, setIsLoadingRecent] = useState(false);
		const [isSearching, setIsSearching] = useState(false);
		const [hasLoadedRecent, setHasLoadedRecent] = useState(false);
		const [lastSelectedOption, setLastSelectedOption] =
			useState<SelectSearchOption | null>(null);

		// Floating UI for dropdown positioning. The shared hook also keeps the
		// menu above the native app's keyboard (PD-14097).
		const { x, y, strategy, refs } = useKeyboardAwareFloating();

		// Size classes
		const sizeClasses = {
			xs: 'px-1.5 py-2 text-xs',
			sm: 'px-2.5 py-1.5 text-sm',
			md: 'px-3 py-2 text-sm',
			lg: 'px-4 py-2.5 text-base',
		}[size];

		// Build all available options for finding selected value
		const allStaticOptions = useMemo(
			() => groups.flatMap((group) => group.options),
			[groups],
		);

		// Create placeholder option memoized to prevent recreating on every render
		const placeholderOption = useMemo(() => {
			if (value != null) {
				return {
					value,
					label: String(value),
				};
			}
			return null;
		}, [value]);

		// Find and set selected option when value changes
		useEffect(() => {
			if (value != null) {
				// First check if we have a lastSelectedOption that matches this value
				if (
					lastSelectedOption &&
					String(lastSelectedOption.value) === String(value)
				) {
					setSelectedOption((current) => {
						// Only update if different
						if (
							current?.value === lastSelectedOption.value &&
							current?.label === lastSelectedOption.label
						) {
							return current;
						}
						return lastSelectedOption;
					});
					return;
				}

				// Otherwise check static groups
				let found = allStaticOptions.find(
					(opt) => String(opt.value) === String(value),
				);

				// If not found and in dynamic mode, check recent items
				if (!found && mode === 'dynamic') {
					found = recentItems.find(
						(opt) => String(opt.value) === String(value),
					);
				}

				// If not found and in dynamic mode, check search results
				if (!found && mode === 'dynamic') {
					found = searchResults.find(
						(opt) => String(opt.value) === String(value),
					);
				}

				// If still not found in dynamic mode, try loadItemById first, then search
				if (!found && mode === 'dynamic' && value) {
					if (loadItemById) {
						loadItemById(String(value))
							.then((item) => {
								if (item) {
									setSelectedOption(item);
									setLastSelectedOption(item);
								} else {
									// Only fall back to placeholder if loadItemById returns null
									setSelectedOption(placeholderOption);
								}
							})
							.catch((err) => {
								console.error('Failed to load item by ID:', err);
								// Fall back to placeholder on error
								setSelectedOption(placeholderOption);
							});

						// Return early - don't set placeholder immediately (wait for async)
						return;
					} else if (searchItems) {
						searchItems(String(value))
							.then((results) => {
								const matchingItem = results.find(
									(opt) => String(opt.value) === String(value),
								);

								if (matchingItem) {
									setSelectedOption(matchingItem);
									setLastSelectedOption(matchingItem);
								} else {
									setSelectedOption(placeholderOption);
								}
							})
							.catch((err) => {
								console.error('Failed to search for item by value:', err);
								setSelectedOption(placeholderOption);
							});

						// Return early - don't set placeholder immediately (wait for async)
						return;
					}
				}

				const newOption = found || placeholderOption;

				// Only update if the option actually changed
				setSelectedOption((current) => {
					if (
						current?.value === newOption?.value &&
						current?.label === newOption?.label
					) {
						return current;
					}

					return newOption;
				});
			} else {
				setSelectedOption(null);
				setLastSelectedOption(null);
			}
		}, [
			value,
			allStaticOptions,
			mode,
			recentItems,
			searchResults,
			placeholderOption,
		]); // Removed lastSelectedOption from deps

		// Load recent items in dynamic mode
		useEffect(() => {
			if (mode === 'dynamic' && loadRecent && !hasLoadedRecent && !disabled) {
				setIsLoadingRecent(true);
				loadRecent()
					.then((items) => {
						setRecentItems(items);
						setHasLoadedRecent(true);
					})
					.catch((err) => {
						console.error('Failed to load recent items:', err);
						setRecentItems([]);
					})
					.finally(() => {
						setIsLoadingRecent(false);
					});
			}
		}, [mode, loadRecent, hasLoadedRecent, disabled]);

		// Use a ref to hold the latest searchItems function so the debounced
		// wrapper stays stable across renders (searchItems changes reference
		// every render because its dependencies are defined inline).
		const searchItemsRef = useRef(searchItems);
		useEffect(() => {
			searchItemsRef.current = searchItems;
		}, [searchItems]);

		// Debounced search for dynamic mode
		const debouncedSearch = useMemo(
			() =>
				debounce(async (searchQuery: string) => {
					if (!searchItemsRef.current || searchQuery.length < minSearchLength) {
						setSearchResults([]);
						setIsSearching(false);
						return;
					}

					setIsSearching(true);
					try {
						const results = await searchItemsRef.current(searchQuery);
						setSearchResults(results);
					} catch (err) {
						console.error('Search failed:', err);
						setSearchResults([]);
					} finally {
						setIsSearching(false);
					}
				}, searchDebounceMs),
			[minSearchLength, searchDebounceMs],
		);

		// Trigger search when query changes
		useEffect(() => {
			if (mode === 'dynamic' && query) {
				// Mark the search in-flight immediately. The debounced runner only
				// sets isSearching once the debounce window elapses, so without this
				// the dropdown renders "No results found" for ~searchDebounceMs
				// before the search even starts (PD-14404).
				if (query.length >= minSearchLength && searchItemsRef.current != null) {
					setIsSearching(true);
				}
				void debouncedSearch(query);
			} else if (mode === 'dynamic') {
				// Cancel any pending debounced run so a stale search doesn't fire
				// after the query was cleared and flip isSearching back on.
				debouncedSearch.cancel();
				setSearchResults([]);
				setIsSearching(false);
			}
		}, [mode, query, debouncedSearch, minSearchLength]);

		// Build effective groups based on mode
		const effectiveGroups = useMemo(() => {
			if (mode === 'static') {
				// Static mode - use provided groups or filter by query
				if (!query) {
					return groups;
				}

				// Filter groups based on query
				return groups
					.map((group) => ({
						...group,
						options: group.options.filter((option) => {
							const searchText =
								`${option.label} ${option.subtitle ?? ''} ${option.detail ?? ''} ${option.badge?.label ?? ''}`.toLowerCase();
							return searchText.includes(query.toLowerCase());
						}),
					}))
					.filter((group) => group.options.length > 0);
			} else {
				// Dynamic mode - build groups from static + recent/search results
				const dynamicGroups: SelectSearchGroup[] = [];

				// Add static groups first (if any)
				if (staticGroups.length > 0) {
					dynamicGroups.push(...staticGroups);
				}

				if (query && query.length >= minSearchLength) {
					// Show search results
					if (searchResults.length > 0) {
						dynamicGroups.push({
							key: 'search',
							label: searchGroupLabel,
							icon: searchGroupIcon,
							options: searchResults,
						});
					}
				} else {
					// Show recent items
					if (recentItems.length > 0) {
						dynamicGroups.push({
							key: 'recent',
							label: recentGroupLabel,
							icon: recentGroupIcon,
							options: recentItems.slice(0, maxRecentItems),
						});
					}
				}

				return dynamicGroups;
			}
		}, [
			mode,
			groups,
			query,
			recentItems,
			searchResults,
			minSearchLength,
			maxRecentItems,
			recentGroupLabel,
			recentGroupIcon,
			searchGroupLabel,
			searchGroupIcon,
			staticGroups,
		]);

		// Handle selection
		const handleChange = (option: SelectSearchOption | null) => {
			if (option) {
				setSelectedOption(option);
				setLastSelectedOption(option);
				onChange?.(String(option.value));
				setQuery(''); // Clear search query after selection
			}
		};

		// Blur after selection. Headless UI itself closes the dropdown when a
		// single-mode option is selected (its render prop exposes no `close`
		// function at runtime — the old destructured `close` was undefined and
		// threw on click).
		const handleOptionClick = () => {
			// Blur the input after a small delay to ensure state updates
			setTimeout(() => {
				if (document.activeElement instanceof HTMLElement) {
					document.activeElement.blur();
				}
			}, 10);
		};

		// The module-level bootstrap above guarantees this element exists in
		// the browser; a single lookup lets the null check narrow for
		// createPortal below.
		const portalTarget = document.getElementById('select-search-portal');

		// Determine loading state
		const isLoading =
			mode === 'dynamic' ? isLoadingRecent || isSearching : false;

		return (
			<Combobox
				value={selectedOption}
				onChange={handleChange}
				disabled={disabled}
				by={(a, b) => String(a?.value) === String(b?.value)}
			>
				{({ open }) => (
					<div
						className={cn('relative', fullWidth ? 'w-full' : 'w-auto')}
						ref={refs.setReference}
					>
						<Combobox.Button as="div" className="relative">
							<Combobox.Input
								ref={ref}
								className={cn(
									'relative w-full text-left cursor-pointer',
									clearable && value ? 'pr-16' : 'pr-10',
									sizeClasses,
									colors.classes.formFieldBase,
									error
										? colors.classes.formFieldError
										: colors.classes.formFieldDefault,
									colors.classes.formFieldFocus,
									disabled && colors.classes.formFieldDisabled,
									fullWidth && 'w-full',
									className,
								)}
								placeholder={t(placeholder)}
								displayValue={(option: SelectSearchOption | null) =>
									option?.label ?? ''
								}
								onChange={(event) => {
									setQuery(event.target.value);
									// Fresh text → no active navigation yet.
									hasNavigatedRef.current = false;
								}}
								onKeyDown={(event) => {
									if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
										// Navigating options — Enter should pick the active one.
										hasNavigatedRef.current = true;
									} else if (
										event.key === 'Enter' &&
										onEnterKey != null &&
										!hasNavigatedRef.current &&
										query.trim() !== ''
									) {
										// Submit the raw typed text (e.g. place text search)
										// without waiting for async options or a click.
										event.preventDefault();
										const input = event.currentTarget;
										onEnterKey(query.trim());
										setTimeout(() => {
											input.blur();
										}, 10);

										return;
									}

									event.stopPropagation();
								}}
								onFocus={(event) => {
									const input = event.target as HTMLInputElement;
									requestAnimationFrame(() => {
										input.select();
									});
								}}
								onBlur={() => {
									setQuery('');
								}}
								autoComplete="off"
							/>
							<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
								<FontAwesomeIcon
									icon={
										isLoading ? faSpinner : query ? faSearch : faChevronDown
									}
									className={cn(
										'h-3 w-3',
										colors.classes.textSecondary,
										isLoading && 'animate-spin',
									)}
									aria-hidden="true"
								/>
							</div>
						</Combobox.Button>
						{clearable && value != null && String(value) !== '' && (
							<button
								type="button"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									onChange?.('');
									setSelectedOption(null);
									setQuery('');
								}}
								className={cn(
									'absolute right-6 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center cursor-pointer',
								)}
								disabled={disabled}
								// The icon alone gives the button no name for a screen reader.
								aria-label={t('Clear')}
							>
								<FontAwesomeIcon
									icon={faTimes}
									className={cn(
										'h-3 w-3',
										colors.classes.textSecondary,
										'hover:' + colors.classes.textPrimary,
										'transition-colors',
									)}
									aria-hidden="true"
								/>
							</button>
						)}

						{open &&
							portalTarget != null &&
							createPortal(
								<div
									style={{ pointerEvents: 'auto' }}
									data-select-search-dropdown="true"
								>
									<Transition
										as={Fragment}
										show={open}
										leave="transition ease-in duration-100"
										leaveFrom="opacity-100"
										leaveTo="opacity-0"
										afterLeave={() => {
											setQuery('');
										}}
									>
										<Combobox.Options
											ref={refs.setFloating}
											style={{
												position: strategy,
												top: y ?? 0,
												left: x ?? 0,
												// The reference is always a real DOM element here,
												// never a floating-ui VirtualElement
												width:
													refs.reference.current instanceof HTMLElement
														? refs.reference.current.offsetWidth
														: undefined,
												pointerEvents: 'auto',
											}}
											className={cn(
												'z-[9999] overflow-auto custom-scrollbar rounded-md py-1 text-base sm:text-sm shadow-lg',
												maxHeightClassName,
												colors.classes.selectMenu,
											)}
										>
											{isLoading ? (
												<div
													className={cn(
														'relative cursor-default py-2 px-3 text-center',
														colors.classes.textSecondary,
													)}
												>
													<FontAwesomeIcon
														icon={faSpinner}
														className="animate-spin mr-2"
													/>
													{t(
														mode === 'dynamic' && !query
															? loadingMessage
															: searchingMessage,
													)}
												</div>
											) : effectiveGroups.length === 0 ? (
												<div
													className={cn(
														'relative cursor-default py-2 px-3 text-center',
														colors.classes.textSecondary,
													)}
												>
													{t(
														mode === 'dynamic'
															? query && query.length >= minSearchLength
																? emptySearchMessage
																: emptyRecentMessage
															: emptyMessage,
													)}
												</div>
											) : (
												effectiveGroups.map((group) => (
													<div key={group.key}>
														{/* Group Header */}
														{group.label && (
															<div
																className={cn(
																	'px-3 py-2 text-xs font-semibold uppercase tracking-wide flex items-center gap-2',
																	colors.classes.bgSecondary,
																	colors.classes.textSecondary,
																)}
															>
																{group.icon && (
																	<FontAwesomeIcon
																		icon={group.icon}
																		className="h-3 w-3"
																	/>
																)}
																{t(group.label)}
															</div>
														)}

														{/* Group Options */}
														{group.options.map((option, index) => (
															<Combobox.Option
																key={`${group.key}-${option.value ?? index}`}
																value={option}
																disabled={option.disabled}
																onClick={() => handleOptionClick()}
																className={({ active, selected }) =>
																	cn(
																		'relative py-2 pl-10 pr-4 transition-colors',
																		option.disabled
																			? 'cursor-not-allowed opacity-50'
																			: 'cursor-pointer',
																		selected &&
																			!active &&
																			colors.classes.selectOptionSelected,
																		active && colors.classes.selectOption,
																		!selected &&
																			!active &&
																			colors.classes.textPrimary,
																	)
																}
															>
																{({ selected }) => (
																	<>
																		<div>
																			{option.badge == null ? (
																				<span
																					className={cn(
																						'block truncate',
																						selected
																							? 'font-medium'
																							: 'font-normal',
																					)}
																				>
																					{t(option.label)}
																				</span>
																			) : (
																				<span
																					className={cn(
																						'flex items-center gap-1.5',
																						selected
																							? 'font-medium'
																							: 'font-normal',
																					)}
																				>
																					{/* min-w-0 lets the label truncate inside flex */}
																					<span className="truncate min-w-0">
																						{t(option.label)}
																					</span>
																					<Badge
																						variant={
																							option.badge.variant ?? 'default'
																						}
																						size="xs"
																						pill
																						className="shrink-0"
																					>
																						{t(option.badge.label)}
																					</Badge>
																				</span>
																			)}
																			{option.subtitle != null &&
																				option.subtitle !== '' && (
																					<span
																						className={cn(
																							'block truncate text-xs mt-0.5',
																							colors.classes.textSecondary,
																						)}
																					>
																						{t(option.subtitle)}
																					</span>
																				)}
																			{option.detail != null &&
																				option.detail !== '' && (
																					<span
																						className={cn(
																							'block truncate text-xs mt-0.5',
																							colors.classes.textSecondary,
																						)}
																					>
																						{t(option.detail)}
																					</span>
																				)}
																		</div>
																		{selected && (
																			<span
																				className={cn(
																					'absolute inset-y-0 left-0 flex items-center pl-3',
																					colors.classes.textPrimary,
																				)}
																			>
																				<FontAwesomeIcon
																					icon={faCheck}
																					className="h-3 w-3"
																					aria-hidden="true"
																				/>
																			</span>
																		)}
																	</>
																)}
															</Combobox.Option>
														))}
													</div>
												))
											)}
										</Combobox.Options>
									</Transition>
								</div>,
								portalTarget,
							)}
					</div>
				)}
			</Combobox>
		);
	},
);

SelectSearch.displayName = 'SelectSearch';
